// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
/**
 * Easel Dashboard Page
 *
 * Main dashboard for the Easel portfolio management platform.
 * Shows portfolio preview, stats, and quick actions.
 */

import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Layers,
  FileText,
  ShoppingBag,
  Mail,
  Settings,
  ExternalLink,
  Palette,
  Brush,
  Eye,
  PlusCircle,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Megaphone,
} from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { formatUserName } from '@/lib/utils'
import {
  usePortfolioStatus,
  usePreviewUrl,
  useCollections,
  useJournalPosts,
  useContactInquiries,
  useShopCategories,
} from '../hooks'
import { InquiryStatus } from '../types'
import { PortfolioPreview } from '../components/PortfolioPreview'
import { BioLinkField } from '../components/BioLinkField'
import { InitializePortfolioCard } from '../components/InitializePortfolioCard'
import { GenomeCard } from '../components/GenomeCard'

// =============================================================================
// Stat Card Component
// =============================================================================

interface StatCardProps {
  title: string
  value: number | string
  description?: string
  icon: React.ReactNode
  href?: string
  isLoading?: boolean
}

function StatCard({ title, value, description, icon, href, isLoading }: StatCardProps) {
  const content = (
    <Card className={href ? 'transition-colors hover:bg-accent/50 cursor-pointer' : ''}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className="text-muted-foreground/70">{icon}</div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <div className="text-3xl font-semibold tracking-tight">{value}</div>
        )}
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  )

  if (href) {
    return <Link to={href}>{content}</Link>
  }

  return content
}

// =============================================================================
// Quick Action Card Component
// =============================================================================

interface QuickActionProps {
  title: string
  description: string
  icon: React.ReactNode
  href: string
  variant?: 'default' | 'outline'
}

function QuickAction({ title, description, icon, href, variant = 'outline' }: QuickActionProps) {
  return (
    <Link to={href}>
      <Card
        className={`h-full transition-all hover:shadow-md ${
          variant === 'default'
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'hover:bg-accent/50'
        }`}
      >
        <CardContent className="flex flex-col items-center justify-center p-6 text-center h-full min-h-[140px]">
          <div className="mb-3">{icon}</div>
          <h3 className="font-semibold">{title}</h3>
          <p
            className={`text-sm mt-1 ${
              variant === 'default' ? 'text-primary-foreground/80' : 'text-muted-foreground'
            }`}
          >
            {description}
          </p>
        </CardContent>
      </Card>
    </Link>
  )
}

// =============================================================================
// Studio Widget Components
// =============================================================================

// =============================================================================
// Main Dashboard Component
// =============================================================================

export function EaselDashboard() {
  const { user } = useAuth()
  const { data: portfolioStatus, isLoading: isLoadingStatus } = usePortfolioStatus()
  const { data: previewData } = usePreviewUrl()
  const { data: collectionsData, isLoading: isLoadingCollections } = useCollections({ limit: 1 })
  const { data: postsData, isLoading: isLoadingPosts } = useJournalPosts({ limit: 1 })
  const { data: inquiriesData, isLoading: isLoadingInquiries } = useContactInquiries({
    status: InquiryStatus.NEW,
    limit: 1,
  })
  const { data: shopCategories, isLoading: isLoadingShop } = useShopCategories()

  if (!user) return null

  // Show initialization card if portfolio is not configured
  if (!isLoadingStatus && !portfolioStatus?.configured) {
    return (
      <div className="p-8">
        <div className="space-y-1 mb-8">
          <h1 className="font-serif text-4xl font-semibold tracking-tight">
            Welcome to Easel, {formatUserName(user)}!
          </h1>
          <p className="text-muted-foreground text-base">
            Let's set up your artist portfolio
          </p>
        </div>
        <InitializePortfolioCard />
      </div>
    )
  }

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <h1 className="font-serif text-4xl font-semibold tracking-tight">Creator Studio</h1>
          <p className="text-muted-foreground text-base">
            Your digital identity and growth command center
          </p>
        </div>
        <div className="flex gap-3">
          {previewData?.url && (
            <Button asChild variant="outline" size="sm">
              <a href={previewData.publicUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                View Live Site
              </a>
            </Button>
          )}
          <Button asChild size="sm">
            <Link to="/app/upload">
              <PlusCircle className="mr-2 h-4 w-4" />
              Upload Art
            </Link>
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      {/* The genome gates the social profile, so it belongs where an artist lands. */}
      <GenomeCard />

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Left Column: Stats & Portfolio */}
        <div className="lg:col-span-3 space-y-6">
          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Collections"
              value={collectionsData?.total || 0}
              description="Curated groups"
              icon={<Layers className="h-4 w-4" />}
              href="/app/easel/collections"
              isLoading={isLoadingCollections}
            />
            <StatCard
              title="Journal"
              value={postsData?.total || 0}
              description="Stories"
              icon={<FileText className="h-4 w-4" />}
              href="/app/easel/journal"
              isLoading={isLoadingPosts}
            />
            <StatCard
              title="Shop"
              value={shopCategories?.length || 0}
              description="Categories"
              icon={<ShoppingBag className="h-4 w-4" />}
              href="/app/easel/shop"
              isLoading={isLoadingShop}
            />
            <StatCard
              title="Inquiries"
              value={inquiriesData?.total || 0}
              description="New messages"
              icon={<Mail className="h-4 w-4" />}
              href="/app/easel/contact"
              isLoading={isLoadingInquiries}
            />
          </div>

          {/* Portfolio Preview */}
          <Card className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between py-4">
              <div>
                <CardTitle className="text-xl">Portfolio Preview</CardTitle>
                <CardDescription>Your link-in-bio page. Copy the link to share it.</CardDescription>
                <div className="mt-2">
                  <BioLinkField link={previewData} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <RefreshCw className="h-4 w-4" />
                </Button>
                {previewData?.url && (
                  <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                    <a href={previewData.publicUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0 border-t">
              <PortfolioPreview url={previewData?.url} />
            </CardContent>
          </Card>
        </div>

        {/* Right Column: AI Insights & Quick Actions */}
        <div className="space-y-6">
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Quick Actions
            </h2>
            <div className="grid gap-2">
              <Button asChild variant="outline" className="justify-start h-10 text-sm">
                <Link to="/app/easel/collections/new">
                  <PlusCircle className="mr-2 h-4 w-4" />
                  New Collection
                </Link>
              </Button>
              <Button asChild variant="outline" className="justify-start h-10 text-sm">
                <Link to="/app/easel/journal/new">
                  <FileText className="mr-2 h-4 w-4" />
                  New Journal Post
                </Link>
              </Button>
              <Button asChild variant="outline" className="justify-start h-10 text-sm">
                <Link to="/app/easel/profile">
                  <Palette className="mr-2 h-4 w-4" />
                  Edit Profile
                </Link>
              </Button>
              <Button asChild variant="outline" className="justify-start h-10 text-sm">
                <Link to="/app/easel/appearance">
                  <Brush className="mr-2 h-4 w-4" />
                  Colours &amp; Style
                </Link>
              </Button>
              <Button asChild variant="outline" className="justify-start h-10 text-sm">
                <Link to="/app/easel/site-content">
                  <FileText className="mr-2 h-4 w-4" />
                  Site Content
                </Link>
              </Button>
              <Button asChild variant="outline" className="justify-start h-10 text-sm">
                <Link to="/app/easel/settings">
                  <Settings className="mr-2 h-4 w-4" />
                  Site Settings
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Management Sections */}
      <Card className="bg-muted/30 border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Platform Management</CardTitle>
          <CardDescription>
            Configure your core digital identity and commerce settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Link to="/app/easel/profile">
              <div className="flex items-center gap-3 p-3 rounded-md border bg-white hover:bg-accent/50 transition-colors">
                <div className="p-2 rounded bg-pink-500/10">
                  <Eye className="h-4 w-4 text-pink-500" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium truncate">Link-in-Bio</h3>
                  <p className="text-[11px] text-muted-foreground truncate">Edit bio & social</p>
                </div>
              </div>
            </Link>
            <Link to="/app/marketing/email">
              <div className="flex items-center gap-3 p-3 rounded-md border bg-white hover:bg-accent/50 transition-colors">
                <div className="p-2 rounded bg-orange-500/10">
                  <Mail className="h-4 w-4 text-orange-500" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium truncate">Email Lists</h3>
                  <p className="text-[11px] text-muted-foreground truncate">Manage collectors</p>
                </div>
              </div>
            </Link>
            <Link to="/app/easel/shop">
              <div className="flex items-center gap-3 p-3 rounded-md border bg-white hover:bg-accent/50 transition-colors">
                <div className="p-2 rounded bg-green-500/10">
                  <ShoppingBag className="h-4 w-4 text-green-500" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium truncate">Shop Config</h3>
                  <p className="text-[11px] text-muted-foreground truncate">Directory settings</p>
                </div>
              </div>
            </Link>
            <Link to="/app/easel/settings">
              <div className="flex items-center gap-3 p-3 rounded-md border bg-white hover:bg-accent/50 transition-colors">
                <div className="p-2 rounded bg-gray-500/10">
                  <Settings className="h-4 w-4 text-gray-500" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium truncate">Advanced</h3>
                  <p className="text-[11px] text-muted-foreground truncate">Domain & SEO</p>
                </div>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
